import { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Shield, Clock, Zap, Database, Bug } from 'lucide-react';
import api from '@/api';

const STATUS_CONFIG = {
  pass: { icon: CheckCircle2, color: '#16a34a', bg: 'bg-green-50', border: 'border-green-200', label: 'PASS' },
  warn: { icon: AlertTriangle, color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-200', label: 'WARN' },
  fail: { icon: XCircle, color: '#dc2626', bg: 'bg-red-50', border: 'border-red-200', label: 'FAIL' },
};

const OVERALL_CONFIG = {
  pass: { text: 'All Systems Go', color: '#16a34a', bg: 'bg-green-50', border: 'border-green-300' },
  warn: { text: 'Warnings Detected', color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-300' },
  fail: { text: 'Issues Found', color: '#dc2626', bg: 'bg-red-50', border: 'border-red-300' },
};

export function DeployCheckTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const runCheck = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.adminDeployCheck();
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to run deploy check');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const data = await api.adminDeployLogs(20);
      setLogs(data.logs || []);
    } catch (e) {
      console.error('Failed to load deploy logs:', e);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => { runCheck(); loadLogs(); }, []);

  const overall = report ? (OVERALL_CONFIG[report.overall] || OVERALL_CONFIG.warn) : null;
  const issues = report ? report.checks.filter(c => c.status !== 'pass') : [];

  return (
    <div data-testid="deploy-check-tab" className="mt-2 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-gray-500" />
          <h2 className="text-sm font-bold text-gray-900">Deployment Health Check</h2>
        </div>
        <button data-testid="deploy-check-run" onClick={runCheck} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-60 transition-all">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {loading ? 'Checking...' : 'Run Check'}
        </button>
      </div>

      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 font-medium">{error}</div>}

      {loading && !report && (
        <div className="text-center py-16">
          <Loader2 size={28} className="animate-spin mx-auto text-gray-400 mb-3" />
          <p className="text-xs text-gray-400">Running deployment checks...</p>
        </div>
      )}

      {report && (
        <>
          {/* Overall status banner */}
          <div data-testid="deploy-check-overall" className={`${overall.bg} ${overall.border} border rounded-2xl p-4 flex items-center justify-between`}>
            <div>
              <p className="text-lg font-extrabold" style={{ color: overall.color }}>{overall.text}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {report.summary.pass} passed, {report.summary.warn} warnings, {report.summary.fail} failures
              </p>
            </div>
            <p className="text-[10px] text-gray-400">{new Date(report.timestamp).toLocaleString()}</p>
          </div>

          {/* Issues section */}
          {issues.length > 0 && (
            <div data-testid="deploy-check-issues">
              <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase mb-2">Issues</p>
              <div className="space-y-2">
                {issues.map((check, i) => {
                  const cfg = STATUS_CONFIG[check.status] || STATUS_CONFIG.warn;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} data-testid={`deploy-issue-${i}`} className={`${cfg.bg} ${cfg.border} border rounded-xl p-3 flex items-start gap-2.5`}>
                      <Icon size={16} style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-gray-900">{check.name}</p>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase" style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}>
                            {cfg.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-600 mt-0.5">{check.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All checks */}
          <div>
            <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase mb-2">All Checks ({report.checks.length})</p>
            <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
              {report.checks.map((check, i) => {
                const cfg = STATUS_CONFIG[check.status] || STATUS_CONFIG.warn;
                const Icon = cfg.icon;
                return (
                  <div key={i} data-testid={`deploy-check-${i}`} className="px-4 py-2.5 flex items-center gap-3">
                    <Icon size={15} style={{ color: cfg.color }} className="shrink-0" />
                    <p className="text-xs font-semibold text-gray-900 w-36 shrink-0">{check.name}</p>
                    <p className="text-[11px] text-gray-500 flex-1 truncate">{check.detail}</p>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase shrink-0"
                      style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}>
                      {cfg.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Deploy & Pipeline Logs */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-gray-500" />
            <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase">Recent Activity</p>
          </div>
          <button onClick={loadLogs} disabled={logsLoading}
            className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1">
            {logsLoading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Refresh
          </button>
        </div>
        {logs.length === 0 && !logsLoading && (
          <p className="text-xs text-gray-400 py-4 text-center">No activity logs yet</p>
        )}
        {logs.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
            {logs.map((log, i) => {
              const isError = log.status === 'fail';
              const LogIcon = log.type === 'error' ? Bug
                : log.type === 'pulse_pipeline' ? Zap
                : log.type === 'data_refresh' ? Database
                : log.type === 'deploy_check' ? Shield
                : Clock;
              const iconColor = isError ? '#dc2626' : log.type === 'pulse_pipeline' ? '#7c3aed' : '#6b7280';
              return (
                <div key={i} data-testid={`deploy-log-${i}`} className="px-4 py-2.5 flex items-start gap-3">
                  <LogIcon size={14} style={{ color: iconColor }} className="shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium ${isError ? 'text-red-600' : 'text-gray-900'}`}>{log.detail}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown time'}
                      {' '}&middot;{' '}{log.type.replace('_', ' ')}
                    </p>
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase shrink-0 ${isError ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>
                    {log.status}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Code Update Instructions */}
      <div className="mt-6 bg-gray-50 rounded-xl border border-gray-200 p-4">
        <p className="text-[10px] font-bold text-gray-500 tracking-widest uppercase mb-2">How to Deploy Updates</p>
        <div className="space-y-1.5 text-[11px] text-gray-600 font-mono">
          <p>1. Save to GitHub from Emergent</p>
          <p>2. SSH: <span className="text-gray-900">ssh -i wtfh-key.pem ec2-user@13.62.255.226</span></p>
          <p>3. Pull: <span className="text-gray-900">cd main-2 && git pull</span></p>
          <p>4. Build: <span className="text-gray-900">docker-compose up -d --build</span></p>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">Quick restart (no rebuild): <span className="font-mono text-gray-500">docker-compose restart backend</span></p>
      </div>
    </div>
  );
}
